<template>
    <div  class="dataTable chartDataTable" >
        <div v-show="loadingGraphData" class="loading"><div class="loader-bg"></div><span class="loader">Loading...</span></div>
        <div v-if="graphData && graphData.results && graphData.results.length == 0" class="no-data">{{noDataErrorMsg}}</div>
        <div class="chart-container" v-else>
            <div class="visualization-menu">
                <div class="chart-menu">
                    <span class="header">Chart Type</span>
                    <div class="btn-group icon-btn-container">
                        <button type="button" class="btn btn-toggle" @click.prevent="setChartType('bar')" :class="{'active': chartType === 'bar'}">
							<i class="fas fa-signal"></i><label class="visually-hidden">bar</label>
                        </button>
                        <button type="button" class="btn btn-toggle " @click.prevent="setChartType('bar-horizontal')" :class="{'active': chartType === 'bar-horizontal'}">
							<span class="fa-rotate-270" style="display: inline-block;">
								<i class="fas fa-signal fa-flip-vertical"></i><label class="visually-hidden">bar-horizontal</label>
							</span>
                        </button>
                        <button type="button" class="btn btn-toggle " @click.prevent="setChartType('line')" :class="{'active': chartType === 'line'}" :disabled="summaryBy && summaryBy.value !== 'FiscalYear'">
							<i class="fas fa-chart-line"></i><label class="visually-hidden">line</label>
                        </button>
                        <button type="button" class="btn btn-toggle " @click.prevent="setChartType('pie')" :class="{'active': chartType === 'pie'}">
							<i class="fas fa-chart-pie"></i><label class="visually-hidden">pie</label>
                        </button>
                        <button type="button" class="btn btn-toggle" @click.prevent="setChartType('circle')" :class="{'active': chartType === 'circle'}">
							<i class="fa fa-dot-circle"></i><label class="visually-hidden">circle</label>
                        </button>
                    </div>
                </div>
                <div class="chart-menu" v-if="chartType !== 'circle'">
                    <span class="header">Summary By
                        <span v-if="chartType === 'bar-horizontal'">(Chart Y Axis)</span>
                        <span v-else-if="chartType === 'bar' || chartType === 'line'">(Chart X Axis)</span>
                    </span>

                    <b-dropdown :text="summaryBy.name" v-if="summaryBy" variant="light">
                        <template v-for="(summary, index) in summaryByPresets">
                            <b-dropdown-item
                                v-if="checkForFundingIC(summary)"
                                href @click.prevent="setSummaryBy(summary)"
                                v-bind:key="'summary-by' + index" rel="noopener"
                                :class="{'disabled': summary.isDisabled, 'selected': summaryBy && summary.value === summaryBy.value}">{{summary.name}}
                            </b-dropdown-item>
                        </template>
                    </b-dropdown>


                </div>
                <div class="chart-menu" v-if="chartType !== 'circle'">
                    <span class="header">Plot By <span v-if="chartType === 'bar-horizontal'">(Chart X Axis)</span><span v-else-if="chartType === 'bar' || chartType === 'line'">(Chart Y Axis)</span></span>
                    <div id="plotby-vis">
                        <div class="btn-group icon-btn-container" role="group" aria-label="Plot by">
                            <button id="chart-projects" type="button" class="btn btn-toggle button-with-icon" @click.prevent="setPlotBy('count')" :class="{'active': plotBy === 'count'}">
								<i class="fas fa-check button-icon" style="margin-top:3px"></i>Projects
                            </button>
                            <button id="chart-funding" type="button" class="btn btn-toggle button-with-icon" @click.prevent="setPlotBy('funding')" :class="{'active': plotBy === 'funding'}">
                                <i class="fas fa-check button-icon" style="margin-top:3px"></i>Funding
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="(!graphData || (graphData && graphData.results && graphData.results.length === 0)) && !loadingGraphData && chartType !== 'circle'" class="no-data">{{noDataErrorMsg}}</div>
            <div v-if="(!circlesData || (circlesData && circlesData.results && circlesData.results.length === 0)) && !loadingCirclesData && chartType === 'circle'" class="no-data">{{noDataErrorMsg}}</div>
            <div class="field-error-message error-large" v-if="serviceError">
                Unable to load chart data
                <div class="error-code">
                {{serviceError}}
                </div>
            </div>
            <chart class="chart"
                ref="chart" v-if="graphData && graphData.results && graphData.results.length > 0 && chartType !== 'circle'"/>

            <div class="circles-wrapper" v-if="chartType === 'circle'">
                <div v-show="loadingCirclesData" class="loading"><div class="loader-bg"></div><span class="loader">Loading...</span></div>
                <circleCluster class="chart"
                    ref="circles" v-if="circlesData && circlesData.clusters && circlesData.clusters.length > 0" />
                    <div class="note" v-else>
                        <div v-show="!loadingCirclesData"><b>Note: </b>No data found.</div>
                    </div>
            </div>

            <div class="note" v-if="graphData && graphData.results && graphData.results.length > 0 && chartType !== 'circle'"><b>Note: </b>Please note that if the hit list contains both a subproject and its parent grant, the subproject funding is already included in the parent project funding amount.</div>
            <div class="note" v-if="graphData && graphData.results && graphData.results.length > 0 && chartType !== 'circle'"><b>* </b> Funding data available only for NIH, CDC, FDA, AHRQ, and ACF.</div>
        </div>

        <div class="drill-down-header" v-if="loadSlide && chartType !== 'circle'">
            <span class="drill-down-label" v-if="selectedDrillDownRow"><span>{{summaryBy.name}}:</span> {{selectedDrillDownRow.group_by}}
                <template v-if="drillDownData">({{drillDownData.meta.total}} <template v-if="selectedProjectView === 'subproject_count'">Sub </template>Project<template v-if="drillDownData.meta.total>1">s</template>)</template></span>
            <a href class="back-btn" @click.prevent="goBack()">
                Back
            </a>
        </div>

        <div class="tableContainer dataTable" v-if="chartType !== 'circle'">
            <div v-show="loadingDrillDownData" class="loading"><div class="loader-bg"></div><span class="loader">Loading...</span></div>
            <dataTable :table="chartTable" :parentData="{'summaryBy': summaryBy, 'loadSlide': loadSlide}" fromPage="charts" v-if="showDataTable() && !loadingGraphData" ref="dataTable" />

            <div class="drillDownTableContainer">
                <transition name="slide" class="">
                    <dataTable :table="drillDownTable" :parentData="{'summaryBy': summaryBy, 'loadSlide': loadSlide, 'drillDownData': drillDownData}" fromPage="charts" v-if="loadSlide && graphData && graphData.results && graphData.results.length > 0" ref="drillDownTable" />
                </transition>
            </div>
        </div>

        <div class="tableContainer dataTable" v-if="chartType === 'circle'">
            <div v-show="loadingCirclesTableData" class="loading"><div class="loader-bg"></div><span class="loader">Loading...</span></div>
            <dataTable :table="circlesTable" :parentData="{'summaryBy': summaryBy, 'loadSlide': loadSlide, 'circlesTableData': circlesTableData}" v-if="circlesTableData && circlesTableData.results && circlesTableData.results.length > 0" fromPage="circles" ref="circlesTable" />
        </div>

    </div>
</template>

<script>
// @ is an alias to /src
import { mapGetters, mapActions } from 'vuex';
import chart from '@/components/global/chart.vue';
import circleCluster from '@/components/global/circleCluster.vue';
import dataTable from '@/components/global/dataTable.vue';
import { toast } from 'vue3-toastify';
import 'vue3-toastify/dist/index.css';
import bus from '@/shared/event-bus.js';

export default {
    name: 'charts',
    components: {
      chart,
      dataTable,
      circleCluster
    },
    props: [
        'drillDownTable'
    ],
    computed: {
        ...mapGetters([
            'searchMeta',
            'searchResults',
            'graphData',
            'circlesData',
            'searchCriteria',
            'editSearchCriteria',
            'noDataErrorMsg'
        ]),
    },
    data: function () {
        return {
            serverless: false,
            data:null,
            loadSlide: false,
            chartType: 'bar',
            plotBy: 'count',
            loadingDrillDownData: false,
            loadingCirclesData: false,
            loadingCirclesTableData: false,
            selectedDrillDownRow: null,
            serviceError: null,
            chartTable: {
                name: 'Visualization',
                totalCount: 0,
                columns: {
                    GroupBy: {
                        displayName: '',
                        fieldName: 'group_by',
                        rowData: false,
                        sortOrder: 'asc',
                        isVisible: true,
                        isSortable: true,
                        helpText: 'fieldName'
                    },
                    ProjectCount: {
                        displayName: 'Projects',
                        fieldName: 'project_count',
                        formatType: 'count',
                        sortOrder: 'asc',
                        callFunctionOnClick: true,
                        isVisible: true,
                        isSortable: true,
                        helpText: 'Projects'
                    },
                    ProjectFundingAmt: {
                        displayName: 'Total Funding',
                        fieldName: 'project_funding_amt',
                        formatType: '$',
                        sortOrder: 'asc',
                        isVisible: true,
                        isSortable: true,
                        helpText: 'Total Funding'
                    },
                    SubprojectCount: {
                        displayName: 'Sub Projects',
                        fieldName: 'subproject_count',
                        callFunctionOnClick: true,
                        formatType: 'count',
                        sortOrder: 'asc',
                        isVisible: true,
                        isSortable: true,
                        helpText: 'Sub Projects'
                    },
                    SubProjectFundingAmt: {
                        displayName: 'Sub Project Funding',
                        fieldName: 'sub_project_funding_amt',
                        formatType: '$',
                        sortOrder: 'asc',
                        isVisible: true,
                        isSortable: true,
                        helpText: 'Sub Project Funding'
                    },
                },
                totalColumns: 5,
                sortField: "project_count",
                sortOrder: 'desc',
                defaultSortField: "project_count",
                defaultSortOrder: 'desc'
            },
            loadingGraphData: false,
            sharedSpecs: window.sharedSpecs,
            chartOptions: copyData(sharedSpecs.eCharts),
            summaryByPresets: copyData(sharedSpecs.summaryPresets),
            summaryBy: null,
            exportTitleLines: 0,
            drillDownData: {
                meta: null,
                results: null
            },
            circlesTable: null,
            circlesTableData: null,
            selectedApplIds: null
        };
    },
    mounted () {
        // check if search ID exists, otherwise redirect to home page
        if ((this.searchMeta && !this.searchMeta.search_id) || !this.$route.params.search_id) {
            this.$router.push({ path: '/' });
        } else {
            this.summaryBy = this.summaryByPresets[0];
            this.updateGraphSettings();

            this.getGraphData();
            this.circlesTable = copyData(this.drillDownTable);
        }

        this.setFullscreen(true);
        bus.$on('set:projectNumber', (routeName) => {
             this.setProjectPageNumber(routeName);
        });
        bus.$off('update:reviseSearchCharts');
        bus.$on('update:reviseSearchCharts', () => {
            if (this.chartType === 'circle') {
                this.circlesTableData = null;
                this.$parent.getProjectSearchCirclesData(false);
            } else {
                this.graphData = [];
                this.$nextTick(() => {
                    this.goBack();
                    this.$parent.getProjectSearchGraphData(false);
                })
            }
        });
        setTimeout(() => {
            document.querySelector("#scrollTable") ? document.querySelector("#scrollTable").removeAttribute("style") : null;
            bus.$emit('update:resize');
        }, 1);
    },
    methods: {
        ...mapActions([
            'setFullscreen',
            'setSearchMode',
            'setGraphData',
            'setGraphSettings'
        ]),
        showDataTable () {
            return !this.loadSlide &&
                   ((this.chartType !== 'circle' && this.graphData && this.graphData.results && this.graphData.results.length > 0))
        },
        checkForFundingIC (summary) {
            if (summary.value === 'FundingIC') {
                if ((this.searchCriteria.is_agency_funding && !this.searchCriteria.is_agency_admin) || (this.editSearchCriteria.is_agency_funding && !this.editSearchCriteria.is_agency_admin)) {
                    return true;
                }
                return false
            }
            return true;
        },
        goBack () {
            var self = this;

            if (self.$refs.drillDownTable) {
                self.$refs.drillDownTable.table.loadingMoreData = false;
            }
            self.loadSlide = false;
        },
        getGraphData () {
            this.$parent.getProjectSearchGraphData(false);
        },
        initChart (data) {
            this.$nextTick(() => {
                switch (this.summaryBy.value) {
                    case 'FiscalYear':
                        this.chartTable.sortOrder = 'asc';
                        this.chartTable.sortField = 'group_by';
                        this.graphData.results = _.orderBy(this.graphData.results, 'group_by', 'asc');
                        this.chartTable.columns.ProjectCount.callFunctionOnClick = true;
                        this.chartTable.columns.SubprojectCount.callFunctionOnClick = true;
                        break;
                    case 'fundingMechanism':
                    case 'FundingIC':
                        this.chartTable.columns.ProjectCount.callFunctionOnClick = false;
                        this.chartTable.columns.SubprojectCount.callFunctionOnClick = false;
                        break;
                    default:
                        this.chartTable.columns.ProjectCount.callFunctionOnClick = true;
                        this.chartTable.columns.SubprojectCount.callFunctionOnClick = true;
                }
                if (this.$refs.chart) {
                    this.$refs.chart.initChart();
                    if (this.$refs && this.$refs.dataTable) {
                        this.$refs.dataTable.initTableData();
                        this.$nextTick(() => {
                            window.fixHeaders('scrollTable');
                        });
                    }
                }
            });
        },
        initGraph  (data) {
            this.$nextTick(() => {
                if (this.$refs.circles) {
                    this.$refs.circles.initGraph();
                }
            });
        },
        setGraphAxisData () {
            var xAxisData = [],
                column1Data = [],
                column2Data = [],
                graphData = this.graphData.results,
                seriesData = [],
                groupByData = [],
                column1Name = this.plotBy === 'count' ? 'project_count' : 'project_funding_amt',
                column2Name = this.plotBy === 'count' ? 'subproject_count' : 'sub_project_funding_amt',
                textFormat = 'normal 15px Roboto',
                textWidth = 0,
                textVal = 0,
                nameGap = 0;

            if (this.chartType === 'bar-horizontal') {
                for (let i = graphData.length - 1; i >= 0; i--) {
                    if (this.chartType === 'pie') {
                        xAxisData.push({
                            value: graphData[i][column1Name],
                            name: graphData[i].group_by
                        });
                        groupByData.push(graphData[i].group_by);
                    } else {
                        if (this.chartType === 'bar-horizontal') {
                            textFormat = 'normal 16px Roboto';
                            textWidth = window.getTextWidth(graphData[i].group_by.toLowerCase(), textFormat);
                        } else {
                            textWidth = window.getTextWidth(this.formatter(graphData[i][column1Name]), textFormat);
                        }
                        if (nameGap < Math.ceil(textWidth)) {
                            nameGap = copyData(Math.ceil(textWidth));
                        }
                        column1Data.push(graphData[i][column1Name]);
                        column2Data.push(graphData[i][column2Name]);
                        groupByData.push(graphData[i].group_by);
                    }
                }
            } else {
                for (let i = 0; i < graphData.length; i++) {
                    if (this.chartType === 'pie') {
                        xAxisData.push({
                            value: graphData[i][column1Name],
                            name: graphData[i].group_by
                        });
                        groupByData.push(graphData[i].group_by);
                    } else {
                        if (this.chartType === 'bar-horizontal') {
                            textFormat = 'normal 16px Roboto';
                            textWidth = window.getTextWidth(graphData[i].group_by.toLowerCase(), textFormat);
                        } else {
                            textWidth = window.getTextWidth(this.formatter(graphData[i][column1Name]), textFormat);
                        }
                        if (nameGap < Math.ceil(textWidth)) {
                            nameGap = copyData(Math.ceil(textWidth));
                        }
                        column1Data.push(graphData[i][column1Name]);
                        column2Data.push(graphData[i][column2Name]);
                        groupByData.push(graphData[i].group_by);
                    }
                }
            }

            seriesData.push({
                name: 'Projects',
                type: this.chartType === 'bar-horizontal' ? 'bar' : this.chartType,
                data: column1Data
            },
            {
                name: 'Sub Projects',
                type: this.chartType === 'bar-horizontal' ? 'bar' : this.chartType,
                data: column2Data
            });

            this.chartOptions = copyData(sharedSpecs.eCharts);
            this.chartOptions.tooltip.formatter = (value) => {
                let seriesData = this.chartOptions.series;

                let toolTip = value.name;

                for (let i = 0; i < seriesData.length; i++) {
                    if(!seriesData[i].data[value.dataIndex]) {
                        toolTip += '<br />' + seriesData[i].name + ': 0';
                    } else {
                        toolTip += '<br />' + seriesData[i].name + ': ' + this.formatter(seriesData[i].data[value.dataIndex], 'y');
                    }
                }
                return toolTip;
            };

            this.chartOptions.legend = copyData(sharedSpecs.eCharts.legend);
            switch (this.chartType) {
                case 'bar':
                case 'line':
                    this.chartOptions.grid.left = nameGap + 10;
                    this.chartOptions.xAxis = {
                        type: 'category',
                        data: groupByData,
                        name: this.summaryBy.name,
                        nameLocation: 'center',
                        nameTextStyle: {
                            fontWeight: 'bold',
                            fontSize:'15',
                        },
                        nameGap: 25,
                        boundaryGap: this.chartType === 'line' ? false : true
                    };
                    this.chartOptions.yAxis = {
                        type: 'value',
                        name: this.plotBy === 'count' ? 'Projects' : 'Funding',
                        nameLocation: 'center',
                        nameTextStyle: {
                            fontWeight: 'bold',
                            fontSize:'15'
                        },
                        axisLabel: {
                            formatter: (value) => {
                                if (value > textVal) {
                                    textVal = value;
                                    nameGap = copyData(Math.ceil(window.getTextWidth(this.formatter(textVal, true), textFormat)));
                                    this.$nextTick( () => {
                                        this.chartOptions.yAxis.nameGap = nameGap + 10;
                                        this.$refs.chart.eChart.setOption(this.chartOptions, true);
                                    });
                                }
                                return this.formatter(value, 'y');
                            }
                        },
                        nameGap: nameGap
                    };
                    break;
                case 'bar-horizontal':
                    this.chartOptions.grid.left = 45;
                    this.chartOptions.grid.right = 55;
                    this.chartOptions.xAxis = {
                        type: 'value',
                        name: this.plotBy === 'count' ? 'Projects' : 'Funding',
                        nameLocation: 'center',
                        nameTextStyle: {
                            fontWeight: 'bold',
                            fontSize:'15'
                        },
                        axisLabel: {
                            formatter: (value) => {
                                return this.formatter(value, 'y');
                            },
                            rotate: 0
                        },
                        nameGap: 25,
                    };
                    this.chartOptions.yAxis = {
                        type: 'category',
                        data: groupByData,
                        name: this.summaryBy.name,
                        nameLocation: 'center',
                        nameTextStyle: {
                            fontWeight: 'bold',
                            fontSize:'15'
                        },
                        nameGap: nameGap + 10,
                    };
                    this.chartOptions.dataZoom = [
                        {
                            show: true,
                            start: 75,
                            end: 100,
                            yAxisIndex: 0,
                            filterMode: 'empty',
                            width: 30,
                            height: '80%',
                            right: 10,
                            moveOnMouseWheel: true
                        }
                    ];
                    break;
                case 'pie':
                    seriesData = [];
                    this.chartOptions.tooltip = {
                        trigger: 'item',
                        showContent: true,
                        formatter: (value) => {
                            return value.data.name + "<br /> " + this.formatter(value.data.value) + " (" + value.percent + "%)";
                        }
                    },
                    this.chartOptions.legend = {
                        orient: 'vertical',
                        x: 'left',
                        data: groupByData,
                        show: false
                    }
                    this.chartOptions.xAxis = {
                        show: false
                    };
                    this.chartOptions.yAxis = {
                        show: false
                    };
                    this.chartOptions.calculable = true;
                    seriesData.push({
                        name:'',
                        type:'pie',
                        radius: ['50%', '70%'],
                        startAngle: 0,
                        avoidLabelOverlap: true,
                        legendHoverLink: true,
                        label: {
                            normal: {
                                show: true
                            }
                        },
                        emphasis: {
                            label: {
                                show: true,
                                position: 'center',
                                textStyle: {
                                    fontSize: '15',
                                    fontWeight: 'bold'
                                }
                            }
                        },
                        labelLine: {
                            normal : {
                                show : true
                            }
                        },
                        data: xAxisData
                    });
                    break;

            }

            this.chartOptions.series = seriesData;

            this.exportChartOptions = copyData(this.chartOptions);
        },
        insertLineBreaks (text, maxWidth, textFormat) {
            let textArray,
                textLine,
                finalText = '';

            if (window.getTextWidth(text, textFormat) > maxWidth) {
                textArray = text.split(' ');
                textLine = '';

                this.exportTitleLines = 1;

                for (let i = 0; i < textArray.length; i++) {
                    if (window.getTextWidth(textLine + ' ' + textArray[i], textFormat) < maxWidth) {
                        textLine += ' ' + textArray[i];
                    } else {
                        finalText += textLine + '\n';
                        textLine = textArray[i];

                        this.exportTitleLines++;
                    }
                }

                finalText += textLine + '\n';

                return finalText.trim();
            }

            return text.trim();
        },
        formatter (value, axis) {
            if (this.plotBy === 'funding') {
                return window.filters.formatLargeCurrency(value);
            } else {
                return window.filters.addCommas(value);
            }
        },
        setSummaryBy (chartXAxis) {
            if (!chartXAxis.isDisabled) {
                this.loadSlide = false;
                this.goBack();
                this.summaryBy = chartXAxis;
                this.chartTable.sortOrder = this.summaryBy.value !== 'FiscalYear' ? this.chartTable.defaultSortOrder : 'group_by';
                this.chartTable.sortField = this.summaryBy.value !== 'FiscalYear' ? this.chartTable.defaultSortField : 'asc';

                // if switching to a Summary var which does not support
                // line charts, switch to bar chart
                if (this.summaryBy.value !== 'FiscalYear' && this.chartType === 'line') {
                    this.chartType = 'bar';
                }
				
				gtag('event','Click', {
					'event_category': 'Charts - Set Summary By',
					'event_label': 'Summary By: ' + chartXAxis,
					'value': 'Summary By: ' + chartXAxis
				});					

                this.updateGraphSettings();
                this.getGraphData();
            }
        },
        setChartType (value) {
            this.chartType = value;
            this.goBack();
            this.$nextTick(() => {
                if (value === 'circle') {
                    this.circlesTableData = null;
                    if (!this.circlesData) {
                        this.$parent.getProjectSearchCirclesData(false);
                    } else {
                        if (this.$refs.circles) {
                            this.$refs.circles.initGraph();
                        }
                    }
                } else {
                    if (!this.graphData) {
                        this.$parent.getProjectSearchGraphData(false);
                    } else {
                        if (this.$refs && this.$refs.chart) {
                            this.$refs.chart.initChart();
                        }
                        if (this.$refs && this.$refs.dataTable) {
                            this.$refs.dataTable.initTableData();
                        }
                    }
                }
            });
			
			gtag('event','Click', {
				'event_category': 'Charts - Set Chart Type',
				'event_label': 'Chart Type: ' + value,
				'value': value
			});	
        },
        setPlotBy (value) {
            var tableData = this.$store.state.graphData.results;

            this.plotBy = value;
            this.updateGraphSettings();
            if (value === 'count') {
                this.chartTable.sortOrder = 'asc';
                this.chartTable.sortField = 'project_count';
            } else {
                this.chartTable.sortOrder = 'asc';
                this.chartTable.sortField = 'project_funding_amt';
            }

            if (this.$refs.dataTable) {
                this.$refs.dataTable.tableData = _.orderBy(tableData, this.chartTable.sortField, this.chartTable.sortOrder);
            }
            this.$nextTick(() => {
                window.fixHeaders('scrollTable');
            });

            if (this.$refs.chart) {
                this.$refs.chart.buildChart();
            }
			
			gtag('event','Click', {
				'event_category': 'Charts - Set Plot By',
				'event_label': 'Plot By: ' + value,
				'value': value
			});	           
        },
        updateGraphSettings () {
            this.setGraphSettings({
                plotBy: this.plotBy,
                summaryBy: this.summaryBy
            });
        },
        setSortDetails ($event, selectedSortColumn) {
            if (this.loadSlide) {
                var selectedSortField = this.$parent.getFieldNameFromSettings(selectedSortColumn)

                if (this.drillDownTable.sortField === selectedSortField) {
                    this.drillDownTable.sortOrder = (this.drillDownTable.sortOrder === 'asc' ? 'desc' : 'asc');
                } else {
                    this.drillDownTable.sortOrder = selectedSortColumn.sortOrder;
                }
                this.drillDownTable.sortField = selectedSortField;
                this.drillDownTable.offset = 0;
                this.$parent.getProjectsForCharts(this.handleDrillDownData);
				
				gtag('event','Click', {
					'event_category':  'Charts - Sorting',
					'event_label':'Drill Down Results - ' + selectedSortField + ' - ' + this.drillDownTable.sortOrder,
					'value': selectedSortField + ' - ' + this.drillDownTable.sortOrder
				});	
            } else {
                if (this.chartType === 'circle') {
                    var selectedSortField = this.$parent.getFieldNameFromSettings(selectedSortColumn)

                    if (this.circlesTable.sortField === selectedSortField) {
                        this.circlesTable.sortOrder = (this.circlesTable.sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                        this.circlesTable.sortOrder = selectedSortColumn.sortOrder;
                    }
                    this.circlesTable.sortField = selectedSortField;
                    this.circlesTable.offset = 0;
                    this.$parent.getProjectsForCircles(this.handleCirclesData);
					
					gtag('event','Click', {
						'event_category': 'Charts - Sorting',
						'event_label': 'Circles Results - ' + selectedSortField + ' - ' + this.circlesTable.sortOrder,
						'value': selectedSortField + ' - ' + this.circlesTable.sortOrder
					});	
                } else {
                    var tableData = this.$store.state.graphData.results;

                    if (this.chartTable.sortField === selectedSortColumn.fieldName) {
                        this.chartTable.sortOrder = this.chartTable.sortOrder === 'asc' ? 'desc' : 'asc';
                    }
                    this.chartTable.sortField = selectedSortColumn.fieldName;
                    this.$refs.dataTable.tableData = _.orderBy(tableData, selectedSortColumn.fieldName, this.chartTable.sortOrder);
					
					gtag('event','Click', {
						'event_category': 'Charts - Sorting',
						'event_label': 'Chart table - ' + selectedSortColumn.fieldName + ' - ' + this.chartTable.sortOrder,
						'value': selectedSortColumn.fieldName + ' - ' + this.chartTable.sortOrder
					});	
                }
                this.chartTable.isLoading = false;
            }
            this.$nextTick(() => {
                window.fixHeaders('scrollTable');
            });
        },
        export (dataType) {
            var data;

            if (dataType === 'chart') {
                if (this.chartType === 'circle') {
                    this.$refs.circles.buildExportGraph();
                } else {
                    this.$refs.chart.buildExportChart('Summary By '+ this.summaryBy.name);
                }
            } else {
                if (this.chartType !== 'circle') {
                    data = this.graphData.results.concat(this.graphData.footerData);
                    window.exportDataToCSV('Summary By '+ this.summaryBy.name, this.chartTable.columns, data);
                }
            }
        },
        loadDrillDownTableData (row, fieldName) {
            var filterBy,
                filterByFacets = copyData(this.$parent.searchFacetCriteria),
                parsedCriteria,
                criteria;

            if (!this.$parent.selectedPerson) {
                if (!this.$store.state.editSearchResults) {
                    parsedCriteria = window.parseCriteria(this.searchCriteria, {isSubmitForm: false});
                    filterBy = window.stringifySearchCriteriaFields(parsedCriteria);
                } else {
                    filterBy =  window.stringifySearchCriteriaFields(this.editSearchCriteria);
                }
            } else {
                let searchCriteriaTemp = {
                    fiscal_years: "ap",
                    pi_profile_ids: this.$parent.selectedPerson.role === 'pi' ? this.$parent.selectedPerson.profile_id : null,
                    po_names: this.$parent.selectedPerson.role === 'po' ? this.$parent.selectedPerson.name : null
                }

                parsedCriteria = window.parseCriteria(searchCriteriaTemp, {isSubmitForm: false});
                filterBy = window.stringifySearchCriteriaFields(parsedCriteria);
            }

            this.selectedDrillDownRow = row;
            switch (this.summaryBy.value) {
                case 'FiscalYear':
                    filterByFacets.fiscal_years = [];
                    filterByFacets.fiscal_years.push(row.group_by);
                    break;
                case 'Agency':
                case 'FundingIC':
                    filterByFacets.agencies = [];
                    filterByFacets.agencies.push(row.group_by);
                    break;
                case 'organization':
                    filterByFacets.org_names = [];
                    filterByFacets.org_names.push(row.group_by);
                    break;
                case 'StateCode':
                    filterByFacets.states = [];
                    filterByFacets.states.push(row.group_by);
                    break;
                case 'Country':
                    filterByFacets.countries = [];
                    filterByFacets.countries.push(row.group_by);
                    break;
                case 'activityCode':
                    filterByFacets.activity_code = [];
                    filterByFacets.activity_code.push(row.group_by);
                    break;
                case 'congDist':
                    filterByFacets.cong_dists = [];
                    filterByFacets.cong_dists.push(row.group_by);
                    break;
                case 'PrincipalInvestigator':
                    filterByFacets.principal_investigators = [];
                    filterByFacets.principal_investigators.push(row.group_by);
                    break;
                case 'fundingMechanism':
                    filterByFacets.funding_mechanism = [];
                    filterByFacets.funding_mechanism.push(row.group_by);
                    break;
                default:
                    toast.error(`<strong>Error</strong>\nInformation is not available for ` + this.summaryBy.name, {
                        autoClose: 2000, 
                        dangerouslyHTMLString: true,
                        closeButton: true
                    });
                    return false;
            }
			
			gtag('event','Click', {
				'event_category': 'Charts',
				'event_label':  'Drill Down Results - ' + row.group_by + ' - ' + fieldName,
				'value': row.group_by + ' - ' + fieldName
			});	

            this.loadingDrillDownData = true;
            this.drillDownTable.columns.actionItems.isVisible = false;
            this.drillDownTable.offset = 0;
            this.$parent.chartProjectsFilterBy = this.$parent.searchFacetCriteria
            this.drillDownTable.sortField = '';
            this.drillDownTable.sortOrder = '';
            this.selectedProjectView = fieldName;
            if (fieldName === 'project_count') {
                filterBy.exclude_subprojects = true;
                filterBy.sub_project_only = false;
            } else {
                filterBy.exclude_subprojects = false;
                filterBy.sub_project_only = true;
            }
            this.$parent.chartProjectsSearchCriteria = copyData(filterBy);
            this.$parent.chartProjectsFilterBy = copyData(filterByFacets);
            this.$parent.getProjectsForCharts(this.handleDrillDownData);
        },
        handleDrillDownData (data) {
            this.loadSlide = true;
            this.loadingDrillDownData = false;
            this.drillDownTable.isLoading = false;
            if (!data.meta.sorted_by_relevance && this.drillDownTable.sortField === '') {
                this.drillDownTable.sortField = 'name';
                this.drillDownTable.sortOrder = 'asc';
            }
            if (this.drillDownTable.offset === 0) {
                this.drillDownData.results = copyData(data.results);
                this.drillDownData.meta = copyData(data.meta);
            } else {
                if (this.$refs.drillDownTable) {
                    this.$refs.drillDownTable.table.loadingMoreData = false;
                }
                if (this.drillDownData.results) {
                    this.drillDownData.results = this.drillDownData.results.concat(data.results);
                }
            }
            if (this.$refs.drillDownTable) {
                this.$refs.drillDownTable.initTableData();
            }
            this.$nextTick(() => {
                window.fixHeaders('scrollTable');
            });
            setTimeout(() => {
                document.querySelector("#scrollTable") ? document.querySelector("#scrollTable").removeAttribute("style") : null;
                bus.$emit('update:resize');
            }, 1);
        },
        setProjectPageNumber(requestedPage) {
            if (requestedPage === this.$options.name) {
                if (this.chartType === 'circle') {
                    this.circlesTable.loadingMoreData = true;
                    this.circlesTable.offset += this.circlesTable.limit;
					gtag('event','Scroll', {
						'event_category': 'Charts',
						'event_label': 'Circles Results - page no ' + this.circlesTable.offset / this.circlesTable.limit,
						'value': 'page no ' + this.circlesTable.offset / this.circlesTable.limit
					});	
                    this.$parent.getProjectsForCircles(this.handleCirclesData);
                } else if (this.loadSlide) {
                    this.drillDownTable.loadingMoreData = true;
                    if (this.drillDownTable.offset <= this.drillDownData.meta.total) {
                        this.drillDownTable.offset += this.drillDownTable.limit;
                        gtag('event','Scroll', {
                            'event_category': 'Charts',
                            'event_label': 'Drill Down Results - page no ' + this.drillDownTable.offset / this.drillDownTable.limit,
                            'value': 'page no ' + this.drillDownTable.offset / this.drillDownTable.limit
                        });	
                        this.$parent.getProjectsForCharts(this.handleDrillDownData);
                    }
                }
            }
        },
        getCircleTableData () {
            if (!this.circlesTable) {
                this.circlesTable = copyData(this.drillDownTable);
                this.circlesData.offset = 0;
            }
            this.loadingCirclesTableData = true;
            this.$parent.getProjectsForCircles(this.handleCirclesData);
        },
        handleCirclesData (data) {
            this.loadingCirclesTableData = false;
            this.circlesTable.isLoading = false;
            if (!data.meta.sorted_by_relevance && this.circlesTable.sortField === '') {
                this.circlesTable.sortField = 'name';
                this.circlesTable.sortOrder = 'asc';
            }
            if (this.circlesTable.offset === 0) {
                this.circlesTableData = copyData(data);
            } else {
                if (this.$refs.circlesTable && this.$refs.circlesTable.table) {
                    this.$refs.circlesTable.table.loadingMoreData = false;
                }
                this.circlesTableData.results = this.circlesTableData.results.concat(data.results);
            }
            this.$nextTick(() => {
                if (this.$refs.circlesTable) {
                    this.$refs.circlesTable.initTableData();
                }
            });
            this.$nextTick(() => {
                window.fixHeaders('scrollTable');
                document.querySelector("#scrollTable") ? document.querySelector("#scrollTable").removeAttribute("style") : null;
                bus.$emit('update:resize');
            });
        },
    }
}
</script>

<style lang="scss" scoped>
@import '@/scss/variables.scss';
@import '@/scss/charts.scss';
@import 'node_modules/bootstrap/scss/_functions';
@import 'node_modules/bootstrap/scss/_variables';
@import '@/scss/variables.scss';
@import 'node_modules/bootstrap/scss/_mixins';

.chartDataTable {
    position: relative;
    .chart-container {
        z-index: 1;
        background-color: $white;
        width: 100%;
    }
    .title-container{
        display: inline-block;
        .page-title {
            font-size: 28px;
            margin-bottom: 5px;
            margin-right: 15px;

            &:after {
                background-color: $green;
            }
        }
    }

    .sub-title {
        font-size: 16px;
    }

    .widget-button {
        position: relative;
        width: 100%;
        span {
            display: inline-block;
            padding-right: 25px;
            width: 100%;
            padding-bottom: 0;
            font-size: 14px;
            text-align: left;
        }
        .dropdown-icon {
            position: absolute;
            right: 7px;
            top: 7px;
        }

    }

    #dropdownMenuSummaryBy {
        min-width: 232px;
    }

    .loading {
        background-color: $grey;
        opacity: 0.5;
        top: 0;
        left:0;

        .loader {
            top: 15%;
            left: 35%;
        }
    }
}

.circles-wrapper {
    min-height: 426px;
    height: 430px;
    max-height: calc(100% - 30px);
    width: calc(100% - 30px);
    margin: 15px;
    overflow: hidden;
}

.btn-toggle {
    @include button-variant($white, $white);
    border-left: 1px solid #B0B0B0;
    border-right: 1px solid #B0B0B0;

    &:disabled {
        opacity: .65;
    }
    &.active {
        @include button-variant($blue-button, $blue-button);
    }
}

.btn-light {
    border: 1px solid red;
    @include button-variant($white, $white);

    &:hover {
        @include button-variant($blue-button, $blue-button);
    }
}
</style>
